/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  Box,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { OcInternalMode } from "../../types/oc";
import { only2dec, formatEUR } from "../../utils/oc/ocCalc";
import { ocTableHeadSx } from "./ocStyles";

interface OcItemsTableProps {
  mode: OcInternalMode;
  items: any[];
  compactMode: boolean;
  ocDate: string;
  onUpdate: (idx: number, patch: Record<string, unknown>) => void;
  onAdd: () => void;
  onDelete: (idx: number) => void;
}

// Comma → dot for decimal inputs (replicates the original behavior).
const toDecimal = (v: string) => v.replace(",", ".");

const OcItemsTable: React.FC<OcItemsTableProps> = ({
  mode,
  items,
  compactMode,
  ocDate,
  onUpdate,
  onAdd,
  onDelete,
}) => {
  const { t } = useTranslation();

  const renderHeader = () => {
    if (mode === "oc-medios") {
      return (
        <TableRow>
          <TableCell>{t("oc.table.concepto")}</TableCell>
          <TableCell align="center">{t("oc.table.mediaInvestment")}</TableCell>
          <TableCell align="center">{t("oc.table.fee")}</TableCell>
          <TableCell align="center">{t("oc.table.total")}</TableCell>
          <TableCell />
        </TableRow>
      );
    }
    if (mode === "oc-creatividades") {
      return (
        <TableRow>
          <TableCell>{t("oc.table.concepto")}</TableCell>
          <TableCell align="center">{t("oc.table.quantity")}</TableCell>
          <TableCell align="center">{t("oc.table.unitPrice")}</TableCell>
          <TableCell align="center">{t("oc.table.total")}</TableCell>
          <TableCell />
        </TableRow>
      );
    }
    if (mode === "oc-proveedores") {
      return (
        <TableRow>
          <TableCell>{t("oc.table.concepto")}</TableCell>
          <TableCell align="center">{t("oc.table.investment")}</TableCell>
          <TableCell align="center">{t("oc.table.kpi")}</TableCell>
          <TableCell align="center">{t("oc.table.kpiCost")}</TableCell>
          <TableCell align="center">{t("oc.table.result")}</TableCell>
          <TableCell />
        </TableRow>
      );
    }
    // oc-proveedores-incluye
    return (
      <TableRow>
        <TableCell>{t("oc.table.campaign")}</TableCell>
        <TableCell align="center">{t("oc.table.dates")}</TableCell>
        <TableCell align="center">{t("oc.table.whatIncludes")}</TableCell>
        <TableCell align="center">{t("oc.table.cost")}</TableCell>
        <TableCell />
      </TableRow>
    );
  };

  const deleteCell = (idx: number) => (
    <TableCell sx={{ verticalAlign: "top", width: 48 }}>
      <IconButton
        size="small"
        color="error"
        aria-label={t("common.delete")}
        onClick={() => onDelete(idx)}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </TableCell>
  );

  const renderRows = () => {
    if (mode === "oc-medios") {
      return items.map((it, idx) => {
        const total = only2dec(it.inversion) + only2dec(it.fee);
        return (
          <TableRow key={`mf-${idx}`}>
            <TableCell sx={{ verticalAlign: "top", width: "54%" }}>
              <TextField
                fullWidth
                multiline
                size="small"
                value={it.concepto}
                onChange={(e) => onUpdate(idx, { concepto: e.target.value })}
              />
            </TableCell>
            <TableCell sx={{ verticalAlign: "top", width: "14%" }}>
              <TextField
                fullWidth
                size="small"
                inputProps={{
                  inputMode: "decimal",
                  style: { textAlign: "right" },
                }}
                value={it.inversion}
                onChange={(e) =>
                  onUpdate(idx, { inversion: toDecimal(e.target.value) })
                }
              />
            </TableCell>
            <TableCell sx={{ verticalAlign: "top", width: "14%" }}>
              <TextField
                fullWidth
                size="small"
                inputProps={{
                  inputMode: "decimal",
                  style: { textAlign: "right" },
                }}
                value={it.fee}
                onChange={(e) =>
                  onUpdate(idx, { fee: toDecimal(e.target.value) })
                }
              />
            </TableCell>
            <TableCell
              align="right"
              sx={{ verticalAlign: "top", fontWeight: 600 }}
            >
              {formatEUR(total)}
            </TableCell>
            {deleteCell(idx)}
          </TableRow>
        );
      });
    }

    if (mode === "oc-creatividades") {
      return items.map((it, idx) => {
        const total = only2dec(it.cantidad) * only2dec(it.precio);
        return (
          <TableRow key={`cpu-${idx}`}>
            <TableCell sx={{ verticalAlign: "top", width: "50%" }}>
              <TextField
                fullWidth
                multiline
                size="small"
                value={it.concepto}
                onChange={(e) => onUpdate(idx, { concepto: e.target.value })}
              />
            </TableCell>
            <TableCell sx={{ verticalAlign: "top", width: "16%" }}>
              <TextField
                fullWidth
                size="small"
                inputProps={{
                  inputMode: "decimal",
                  style: { textAlign: "right" },
                }}
                value={it.cantidad}
                onChange={(e) =>
                  onUpdate(idx, { cantidad: toDecimal(e.target.value) })
                }
              />
            </TableCell>
            <TableCell sx={{ verticalAlign: "top", width: "16%" }}>
              <TextField
                fullWidth
                size="small"
                inputProps={{
                  inputMode: "decimal",
                  style: { textAlign: "right" },
                }}
                value={it.precio}
                onChange={(e) =>
                  onUpdate(idx, { precio: toDecimal(e.target.value) })
                }
              />
            </TableCell>
            <TableCell
              align="right"
              sx={{ verticalAlign: "top", fontWeight: 600 }}
            >
              {formatEUR(total)}
            </TableCell>
            {deleteCell(idx)}
          </TableRow>
        );
      });
    }

    if (mode === "oc-proveedores") {
      return items.map((it, idx) => (
        <TableRow key={`prov-${idx}`}>
          <TableCell sx={{ verticalAlign: "top", width: "40%" }}>
            <TextField
              fullWidth
              multiline
              size="small"
              value={it.concepto}
              onChange={(e) => onUpdate(idx, { concepto: e.target.value })}
            />
          </TableCell>
          <TableCell sx={{ verticalAlign: "top", width: "14%" }}>
            <TextField
              fullWidth
              size="small"
              inputProps={{
                inputMode: "decimal",
                style: { textAlign: "right" },
              }}
              value={it.inv || ""}
              onChange={(e) =>
                onUpdate(idx, { inv: toDecimal(e.target.value) })
              }
            />
          </TableCell>
          <TableCell sx={{ verticalAlign: "top", width: "10%" }}>
            <TextField
              fullWidth
              size="small"
              value={it.KPI || ""}
              onChange={(e) => onUpdate(idx, { KPI: e.target.value })}
            />
          </TableCell>
          <TableCell sx={{ verticalAlign: "top", width: "12%" }}>
            <TextField
              fullWidth
              size="small"
              inputProps={{
                inputMode: "decimal",
                style: { textAlign: "right" },
              }}
              value={it.costKPI || ""}
              onChange={(e) =>
                onUpdate(idx, { costKPI: toDecimal(e.target.value) })
              }
            />
          </TableCell>
          <TableCell sx={{ verticalAlign: "top", width: "18%" }}>
            <TextField
              fullWidth
              size="small"
              inputProps={{ style: { textAlign: "right" } }}
              value={it.result || ""}
              onChange={(e) => onUpdate(idx, { result: e.target.value })}
            />
          </TableCell>
          {deleteCell(idx)}
        </TableRow>
      ));
    }

    // oc-proveedores-incluye
    return items.map((it, idx) => (
      <TableRow key={`prov-incluye-${idx}`}>
        <TableCell sx={{ verticalAlign: "top", width: "25%" }}>
          <TextField
            fullWidth
            multiline
            size="small"
            value={it.concepto}
            onChange={(e) => onUpdate(idx, { concepto: e.target.value })}
          />
        </TableCell>
        <TableCell sx={{ verticalAlign: "top", width: "23%" }}>
          <RadioGroup
            row
            value={it.useDateRange ? "range" : "text"}
            onChange={(e) =>
              onUpdate(idx, { useDateRange: e.target.value === "range" })
            }
          >
            <FormControlLabel
              value="text"
              control={<Radio size="small" />}
              label={t("oc.table.radioText")}
            />
            <FormControlLabel
              value="range"
              control={<Radio size="small" />}
              label={t("oc.table.radioDates")}
            />
          </RadioGroup>
          {!it.useDateRange ? (
            <TextField
              fullWidth
              size="small"
              placeholder={t("oc.table.tbcPlaceholder")}
              value={it.fechaText || ""}
              onChange={(e) => onUpdate(idx, { fechaText: e.target.value })}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={it.fechaInicio || ocDate}
                onChange={(e) => onUpdate(idx, { fechaInicio: e.target.value })}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: it.fechaInicio }}
                value={it.fechaFin || ""}
                onChange={(e) => onUpdate(idx, { fechaFin: e.target.value })}
              />
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ verticalAlign: "top", width: "35%" }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            placeholder={t("oc.table.includesPlaceholder")}
            value={it.incluye}
            onChange={(e) => onUpdate(idx, { incluye: e.target.value })}
          />
        </TableCell>
        <TableCell sx={{ verticalAlign: "top", width: "12%" }}>
          <TextField
            fullWidth
            size="small"
            inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
            value={it.coste || ""}
            onChange={(e) =>
              onUpdate(idx, { coste: toDecimal(e.target.value) })
            }
          />
        </TableCell>
        {deleteCell(idx)}
      </TableRow>
    ));
  };

  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={(t) => ({ minWidth: 720, ...ocTableHeadSx(t) })}
        >
          <TableHead>{renderHeader()}</TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ width: "fit-content" }}
        >
          {t("oc.table.addLine")}
        </Button>
        {compactMode && (
          <Alert severity="warning" sx={{ maxWidth: 480 }}>
            <Typography variant="pSmall">
              <strong>{t("oc.table.compactTitle")}</strong>{" "}
              {t("oc.table.compactBody")}
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default OcItemsTable;
